"use client";

import type { ToeflTaskType } from "@/lib/exams/toefl/criteria";
import {
  type Stimulus,
  serialiseStimulusForMarking,
  stimuliFor,
  getStimulus,
} from "@/lib/exams/toefl/stimulus";
import type { ExamMarkConfig } from "@/lib/exams/markConfig";
import { ToeflStimulusView } from "./ToeflStimulusView";

export const TOEFL_TASKS = [
  { value: "integrated", label: "Integrated" },
  { value: "discussion", label: "Academic Discussion" },
] as const;

// Coaching-only target scores on the familiar 0–30 section scale (the score
// learners recognise). Never affects marking.
export const TOEFL_TARGET_SCORES = [24, 25, 26, 27, 28, 29, 30] as const;

// The single source of the marker's context string (initial mark AND re-marks).
// Embeds task/target score, the human task instruction, and the marking
// ground-truth (Integrated passage + transcript / Discussion question + posts);
// names the stimulusId for the model to echo so the result re-hydrates on resume.
export const buildToeflMarkPrefix = (
  task: ToeflTaskType,
  targetScore: number | null,
  stimulus: Stimulus,
): string => {
  const groundTruth = serialiseStimulusForMarking(stimulus);
  return [
    `TASK: ${task}`,
    `TARGET SCORE (coaching tone only — must NOT affect marks): ${targetScore ?? "none stated"}`,
    `stimulusId (echo this back verbatim): ${stimulus.id}`,
    `TASK INSTRUCTION: ${stimulus.promptText}`,
    groundTruth ? `\n${groundTruth}` : "",
    "",
  ].join("\n");
};

interface ToeflMarkContextProps {
  task: ToeflTaskType;
  targetScore: number;
  onTaskChange: (t: ToeflTaskType) => void;
  onTargetScoreChange: (s: number) => void;
}

const selectClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white";

function ToeflMarkContext({
  task,
  targetScore,
  onTaskChange,
  onTargetScoreChange,
}: ToeflMarkContextProps) {
  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[140px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Task</label>
        <select
          value={task}
          onChange={(e) => onTaskChange(e.target.value as ToeflTaskType)}
          className={selectClass}
        >
          {TOEFL_TASKS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[100px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Target score</label>
        <select
          value={targetScore}
          onChange={(e) => onTargetScoreChange(Number(e.target.value))}
          className={selectClass}
        >
          {TOEFL_TARGET_SCORES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mark-form config (drives the generic mark page + resume view)
// ---------------------------------------------------------------------------

export interface ToeflSelState {
  task: ToeflTaskType;
  targetScore: number;
  stimulus: Stimulus | null;
}

const firstStimulus = (task: ToeflTaskType): Stimulus | null => stimuliFor(task)[0] ?? null;

function ToeflSelectors({
  state,
  onChange,
}: {
  state: ToeflSelState;
  onChange: (next: ToeflSelState) => void;
}) {
  const setTask = (task: ToeflTaskType) =>
    onChange({ ...state, task, stimulus: firstStimulus(task) });
  const shuffle = () => {
    const list = stimuliFor(state.task);
    if (list.length === 0) return;
    const idx = state.stimulus ? list.findIndex((s) => s.id === state.stimulus!.id) : -1;
    onChange({ ...state, stimulus: list[(idx + 1) % list.length] });
  };
  return (
    <div className="space-y-3">
      <ToeflMarkContext
        task={state.task}
        targetScore={state.targetScore}
        onTaskChange={setTask}
        onTargetScoreChange={(targetScore) => onChange({ ...state, targetScore })}
      />
      {state.stimulus && (
        <div className="space-y-2 rounded-lg border border-brand-soft bg-brand-surface p-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs font-semibold tracking-wide text-brand uppercase">Your task</p>
            <button
              type="button"
              onClick={shuffle}
              className="text-xs text-brand underline hover:opacity-70"
            >
              New prompt
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap text-brand">{state.stimulus.promptText}</p>
          <ToeflStimulusView stimulus={state.stimulus} />
        </div>
      )}
    </div>
  );
}

export const toeflMarkConfig: ExamMarkConfig<ToeflSelState> = {
  defaultState: { task: "integrated", targetScore: 26, stimulus: firstStimulus("integrated") },
  Selectors: ToeflSelectors,
  promptText: (s) => s.stimulus?.promptText ?? null,
  handoffKeys: ["toeflTask", "toeflTargetScore", "toeflStimulusId"],
  buildHandoff: (s): Record<string, string> =>
    s.stimulus
      ? {
          toeflTask: s.task,
          toeflTargetScore: String(s.targetScore),
          toeflStimulusId: s.stimulus.id,
        }
      : {},
  buildPrefix: (h, basePrompt) => {
    const task = h.toeflTask as ToeflTaskType | null;
    const stimulusId = h.toeflStimulusId;
    if (task && stimulusId) {
      const stimulus = getStimulus(task, stimulusId);
      if (stimulus) {
        const targetScore = h.toeflTargetScore ? Number(h.toeflTargetScore) : null;
        return buildToeflMarkPrefix(task, targetScore, stimulus);
      }
    }
    return basePrompt;
  },
};
